import { Drawer } from "@mui/material";
import { Currency } from "@redotech/money/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { RedoFulfillmentLocation } from "@redotech/redo-fulfillment-group-common/fulfillment-location";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { CreateFulfillmentOrder } from "@redotech/redo-fulfillment-operations-common/create-fulfillment-order";
import { IMoney } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import { createAddressAutocompleteForm } from "@redotech/redo-web/address-autocomplete-form";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { DeprecatedDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-date-picker";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/standard-redo-list-items";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { AutoCompleteInput } from "@redotech/redo-web/autocomplete-input";
import { IconButton } from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  groupInput,
  input,
  InputSchema,
  optionalInput,
} from "@redotech/ui/form";
import { memo, useMemo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { AddressForm } from "./address-form";
import { ItemTable } from "./item-table";
import {
  getManualOrderAddressParseRequest,
  parseManualOrderAddress,
} from "./manual-order-address-parser";
import { resolveOriginAddress } from "./manual-order-origin";
import * as styles from "./order-form-slideout.module.css";

function formatCustomerDisplayText(address: FulfillmentOrderAddress): string {
  const name = address.name ?? "";
  const contact = address.email || address.phone || "";
  return contact ? `${name} — ${contact}` : name;
}

const orderInfoForm = groupInput({
  orderNumber: input<string>({}),
  orderDate: input<Date>({}),
  paidDate: input<Date>({}),
  shippingPaid: input<IMoney>({}),
  taxPaid: input<IMoney>({}),
  totalPaid: input<IMoney>({}),
  items: input<FulfillmentOrderLineItem[]>({}),
  internalNote: input<string>({}),
});

export const manualOrderAddressForm = createAddressAutocompleteForm(
  false,
  false,
  { validatePhoneFormat: false },
);

export const manualOrderForm = groupInput({
  location: input<RedoFulfillmentLocation | null>({}),
  // Set when the user picks "+ Custom location". optionalInput keeps its
  // validators dormant until a value is present so it never blocks submit.
  customLocation: optionalInput(manualOrderAddressForm, emptyCustomLocation),
  address: manualOrderAddressForm,
  orderInfo: orderInfoForm,
});
export type ManualOrderForm = InputSchema.Input<typeof manualOrderForm>;
export type ManualOrderFormValue = InputSchema.Value<typeof manualOrderForm>;

export const CUSTOM_LOCATION_OPTION_ID = "__custom_location__";

function locationListItem(
  location: RedoFulfillmentLocation,
  isDefault: boolean,
  useWrapper = true,
): RedoListItem<RedoFulfillmentLocation | null> {
  if (!isDefault) {
    return {
      id: location.externalId,
      type: "text",
      text: location.name,
      value: location,
    };
  }

  return {
    id: location.externalId,
    type: "custom",
    render: (
      <Flex align="center" gap="sm" overflow="hidden">
        <TextRedoListItem size="sm" text={location.name} useWrapper={false} />
        <RedoBadge color="gray" size="xs" text="Default" />
      </Flex>
    ),
    useWrapper,
    value: location,
  };
}

export function emptyCustomLocation(): InputSchema.Value<
  typeof manualOrderAddressForm
> {
  return {
    name: "",
    address1: "",
    address2: "",
    city: "",
    company: "",
    countryCode: RedoShopifyCountryCode.Us,
    email: "",
    phone: "",
    province: "",
    zip: "",
  };
}

export interface ManualOrderSlideoutProps {
  form: ManualOrderForm;
  rpcCall: (
    fulfillmentOrder: CreateFulfillmentOrder,
  ) => Promise<{ success: boolean; fulfillmentGroups: FulfillmentGroupData[] }>;
  onClose: () => void;
  open: boolean;
  title: string;
  submitButtonText: string;
  onSuccess: (groups: FulfillmentGroupData[]) => void;
  successMessage: string;
  errorMessage: string;
  locations: readonly RedoFulfillmentLocation[];
  defaultLocationId?: string | null;
  itemTableResetKey?: number;
}

export const ManualOrderSlideout = memo(function ManualOrderSlideout({
  form,
  rpcCall,
  onSuccess,
  onClose,
  open,
  title,
  submitButtonText,
  successMessage,
  errorMessage,
  locations,
  itemTableResetKey,
  defaultLocationId,
}: ManualOrderSlideoutProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [customerSearch, setCustomerSearch] = useState<string>("");
  const [pastedAddress, setPastedAddress] = useState<string>("");
  const [isParsingAddress, setIsParsingAddress] = useState(false);

  const outboundLabelsRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );
  const inventoryRpcClient = useRequiredContext(RedoInventoryRpcClientContext);

  const getCustomerSuggestions = useHandler(async (query: string) => {
    const { suggestions } = await outboundLabelsRpcClient.searchCustomers({
      searchString: query,
    });
    return suggestions.map((suggestion) => ({
      keyToFullObject: suggestion,
      displayText: formatCustomerDisplayText(suggestion),
    }));
  });

  const pickCustomerSuggestion = useHandler(
    async (address: FulfillmentOrderAddress) => address,
  );

  const applyCustomerToForm = useHandler((address: FulfillmentOrderAddress) => {
    form.inputs.address.setValue({
      name: address.name ?? "",
      address1: address.address1 ?? "",
      address2: address.address2 ?? "",
      city: address.city ?? "",
      province: address.province ?? "",
      zip: address.zip ?? "",
      countryCode:
        (address.countryCode as RedoShopifyCountryCode) ??
        RedoShopifyCountryCode.Us,
      email: address.email ?? "",
      company: address.company ?? "",
      phone: address.phone ?? "",
    });
  });

  const fillAddressFromPaste = useHandler(async () => {
    const parseRequest = getManualOrderAddressParseRequest(pastedAddress);
    if (!parseRequest) {
      toast("Paste an address before filling the form", { variant: "error" });
      return;
    }

    setIsParsingAddress(true);
    try {
      let parsedAddress = parseManualOrderAddress(pastedAddress);
      if (!parsedAddress) {
        const { parsedAddresses } = await inventoryRpcClient.parseAddresses({
          addresses: [parseRequest.address],
        });
        parsedAddress = parseManualOrderAddress(
          pastedAddress,
          parsedAddresses[0],
        );
      }

      if (!parsedAddress) {
        toast("We couldn't parse that address. Please enter it manually.", {
          variant: "error",
        });
        return;
      }

      const nextAddress = {
        ...form.value.address,
        ...parsedAddress,
        countryCode:
          parsedAddress.countryCode ?? form.value.address.countryCode,
        name: parsedAddress.name || form.value.address.name,
      };
      form.inputs.address.inputs.name.setValue(nextAddress.name);
      form.inputs.address.inputs.address1.setValue(nextAddress.address1);
      form.inputs.address.inputs.address2.setValue(nextAddress.address2);
      form.inputs.address.inputs.city.setValue(nextAddress.city);
      form.inputs.address.inputs.countryCode.setValue(nextAddress.countryCode);
      // The province select maps "no province" to null; "" leaves it blank.
      form.inputs.address.inputs.province.setValue(
        nextAddress.province || null,
      );
      form.inputs.address.inputs.zip.setValue(nextAddress.zip);
      if (parsedAddress.email) {
        form.inputs.address.inputs.email.setValue(parsedAddress.email);
      }
      if (parsedAddress.phone) {
        form.inputs.address.inputs.phone.setValue(parsedAddress.phone);
      }
    } catch (error) {
      console.error("Failed to parse pasted address", error);
      toast("We couldn't parse that address. Please enter it manually.", {
        variant: "error",
      });
    } finally {
      setIsParsingAddress(false);
    }
  });

  const isCustomLocation = form.value.customLocation !== undefined;

  const isSubmitDisabled = useMemo(() => {
    return form.allErrors.length > 0;
  }, [form]);

  const handleSubmit = useHandler(async () => {
    try {
      setLoading(true);
      // Update the total prices based off of the changes to the quantity
      // Unit discount price and total discount price are the same as the unit price and total price
      const data: CreateFulfillmentOrder = {
        address: form.value.address,
        location: resolveOriginAddress(
          form.value.location,
          form.value.customLocation,
        ),
        orderInfo: {
          ...form.value.orderInfo,
          items: form.value.orderInfo.items.map((item) => ({
            ...item,
            unitDiscountedPrice: {
              amount: item.unitPrice.amount,
              currency: item.unitPrice.currency,
            },
            totalPrice: {
              amount: (
                Number(item.unitPrice.amount) * item.totalQuantity
              ).toString(),
              currency: item.unitPrice.currency,
            },
            totalDiscountedPrice: {
              amount: (
                Number(item.unitPrice.amount) * item.totalQuantity
              ).toString(),
              currency: item.unitPrice.currency,
            },
            unfulfilledQuantity: item.totalQuantity,
            internalNotes: form.value.orderInfo.internalNote,
          })),
        },
      };

      const { success, fulfillmentGroups } = await rpcCall(data);

      if (success) {
        toast(successMessage, { variant: "success" });
        onClose();
        onSuccess(fulfillmentGroups);
      } else {
        toast(errorMessage, { variant: "error" });
      }
    } catch (error) {
      toast(errorMessage, { variant: "error" });
      console.error(error);
    } finally {
      setLoading(false);
    }
  });

  return (
    <Drawer
      anchor="right"
      onClose={onClose}
      open={open}
      sx={{ "& .MuiDrawer-paper": { overflow: "hidden" } }}
    >
      <Flex className={styles.drawerContainer} dir="column">
        <Flex className={styles.header} dir="column" gap="none">
          <Flex
            align="flex-start"
            borderBottomWidth="1px"
            borderColor="primary"
            borderStyle="solid"
            dir="row"
            justify="space-between"
            p="3xl"
          >
            <Flex dir="column" gap="xs">
              <Text fontSize="xl" fontWeight="semibold">
                {title}
              </Text>
            </Flex>
            <IconButton aria-label="Close" onClick={onClose}>
              <XIcon />
            </IconButton>
          </Flex>
        </Flex>
        <Flex className={styles.content} dir="column" gap="none" w="full">
          <Flex dir="row" gap="none" justify="space-between" w="full">
            <Flex
              className={styles.orderFormColumn}
              dir="column"
              flex="1"
              gap="3xl"
              p="3xl"
            >
              <Flex dir="column" gap="sm">
                <RedoTextareaInputField
                  autoExpand
                  fullWidth
                  label="Paste recipient address"
                  placeholder={
                    "Jamie Rivera\n123 Main Street\nSuite 400\nAustin, TX 78701"
                  }
                  setValue={setPastedAddress}
                  value={pastedAddress}
                />
                <Flex justify="flex-end">
                  <RedoButton
                    disabled={!pastedAddress.trim()}
                    hierarchy="secondary"
                    onClick={fillAddressFromPaste}
                    pending={isParsingAddress}
                    size="sm"
                    text="Fill address fields"
                    theme="normal"
                  />
                </Flex>
              </Flex>
              <AutoCompleteInput<
                FulfillmentOrderAddress,
                FulfillmentOrderAddress
              >
                getSuggestions={getCustomerSuggestions}
                label="Search for customer"
                pickSuggestion={pickCustomerSuggestion}
                placeholder="Search by name, email, or phone"
                setAutocompleteFields={applyCustomerToForm}
                setValue={setCustomerSearch}
                value={customerSearch}
              />
              <AddressForm
                form={form.inputs.address}
                requirePhone={false}
                title="Recipient information"
              />
            </Flex>
            <Flex
              bgColor="tertiary"
              className={styles.orderFormColumn}
              dir="column"
              flex="1"
              gap="3xl"
              p="3xl"
            >
              <Text fontSize="md" fontWeight="semibold">
                Order details
              </Text>
              <RedoTextInput
                label="Order #"
                placeholder="Order number will be generated if not provided"
                setValue={form.inputs.orderInfo.inputs.orderNumber.setValue}
                value={form.value.orderInfo.orderNumber}
              />
              <Flex dir="column" gap="xl">
                <RedoSingleSelectDropdownInput<RedoFulfillmentLocation | null>
                  label="Location"
                  options={[
                    ...locations.map((location) =>
                      locationListItem(
                        location,
                        location.externalId === defaultLocationId,
                      ),
                    ),
                    {
                      id: CUSTOM_LOCATION_OPTION_ID,
                      type: "text" as const,
                      text: "+ Custom location",
                      value: null,
                    },
                  ]}
                  optionSelected={({ item }) => {
                    if (item.id === CUSTOM_LOCATION_OPTION_ID) {
                      form.inputs.location.setValue(null);
                      form.inputs.customLocation.setValue(
                        emptyCustomLocation(),
                      );
                    } else {
                      form.inputs.customLocation.setValue(undefined);
                      form.inputs.location.setValue(item.value);
                    }
                  }}
                  selectedItem={
                    isCustomLocation
                      ? {
                          id: CUSTOM_LOCATION_OPTION_ID,
                          type: "text",
                          text: "Custom location",
                          value: null,
                        }
                      : form.value.location
                        ? locationListItem(
                            form.value.location,
                            form.value.location.externalId ===
                              defaultLocationId,
                            false,
                          )
                        : undefined
                  }
                  size="xs"
                />
                {form.inputs.customLocation.input && (
                  <AddressForm
                    form={form.inputs.customLocation.input}
                    hideEmailField
                    requirePhone={false}
                    title="Custom location"
                  />
                )}
              </Flex>
              <Flex dir="column" gap="xs">
                <Text fontSize="xs" textColor="tertiary">
                  Order date
                </Text>
                <DeprecatedDatePicker
                  buttonSize="sm"
                  date={form.value.orderInfo.orderDate}
                  maxDate={new Date()}
                  setDate={(date) => {
                    form.inputs.orderInfo.inputs.orderDate.setValue(date);
                  }}
                />
              </Flex>
              <Flex dir="column" gap="xs">
                <Text fontSize="xs" textColor="tertiary">
                  Paid date
                </Text>
                <DeprecatedDatePicker
                  buttonSize="sm"
                  date={form.value.orderInfo.paidDate}
                  maxDate={new Date()}
                  setDate={(date) => {
                    form.inputs.orderInfo.inputs.paidDate.setValue(date);
                  }}
                />
              </Flex>
              <RedoIncrementDecrement
                fullWidth
                label="Shipping paid (USD)"
                min={0}
                setValue={(value) => {
                  form.inputs.orderInfo.inputs.shippingPaid.setValue({
                    amount: value.toString(),
                    currency: Currency.USD,
                  });
                }}
                size="small"
                value={Number(form.value.orderInfo.shippingPaid.amount)}
              />
              <RedoIncrementDecrement
                fullWidth
                label="Tax paid (USD)"
                min={0}
                setValue={(value) => {
                  form.inputs.orderInfo.inputs.taxPaid.setValue({
                    amount: value.toString(),
                    currency: Currency.USD,
                  });
                }}
                size="small"
                value={Number(form.value.orderInfo.taxPaid.amount)}
              />
              <RedoIncrementDecrement
                fullWidth
                label="Total paid (USD)"
                min={0}
                setValue={(value) => {
                  form.inputs.orderInfo.inputs.totalPaid.setValue({
                    amount: value.toString(),
                    currency: Currency.USD,
                  });
                }}
                size="small"
                value={Number(form.value.orderInfo.totalPaid.amount)}
              />
              <RedoTextInput
                label="Internal notes"
                placeholder="Enter internal notes"
                setValue={form.inputs.orderInfo.inputs.internalNote.setValue}
                value={form.value.orderInfo.internalNote}
              />
            </Flex>
          </Flex>
          <ItemTable
            initialItems={form.value.orderInfo.items}
            key={itemTableResetKey}
            onItemsChange={(items) => {
              form.inputs.orderInfo.inputs.items.setValue(items);
            }}
          />
        </Flex>
        <Flex
          align="center"
          borderBottomWidth="1px"
          borderColor="primary"
          borderStyle="solid"
          borderTopWidth="1px"
          className={styles.footer}
          dir="row"
          justify="space-between"
          p="3xl"
          w="full"
        >
          <RedoButton
            className={styles.cancelButton}
            hierarchy="secondary"
            onClick={onClose}
            size="md"
            text="Cancel"
            theme="normal"
          />
          <RedoButton
            className={styles.createButton}
            disabled={isSubmitDisabled}
            hierarchy="primary"
            onClick={handleSubmit}
            pending={loading}
            size="md"
            text={submitButtonText}
            theme="normal"
            type="submit"
          />
        </Flex>
      </Flex>
    </Drawer>
  );
});
